/**
 * @file HOC acting as react-modal wrapper.
 */

import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Modal from 'react-modal';

import { closeModal } from '../../../actions/modal-actions';

const withModal = (
  WrappedComponent,
  { name, className, overlayClassName, getClassNames, ...modalProps },
) => {
  class ModalWrapperContainer extends Component {
    static propTypes = {
      onRequestClose: PropTypes.func.isRequired,
      actionProps: PropTypes.shape({
        actionSize: PropTypes.string,
        modalTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      }),
      isOpen: PropTypes.bool,
      forceOpen: PropTypes.bool,
      onClose: PropTypes.func,
    };

    onClose = (...args) => {
      this.props.onRequestClose();

      const { onClose, actionProps = {} } = this.props;
      const { onDismiss } = actionProps;

      if (onClose) {
        onClose(...args);
      }

      onDismiss?.();
    };

    render() {
      const { contentLabel, size, type } = modalProps;
      const { actionProps = {} } = this.props;
      const {
        forcedTopPosition,
        modalTitle = contentLabel,
        actionSize,
        actionDismissible,
        actionHideHeader,
        timeout,
      } = actionProps;
      // If actionDismissible is passed, prioritize it.
      const dismissible =
        typeof actionDismissible !== 'undefined'
          ? !!actionDismissible
          : modalProps.dismissible !== false;

      const hideHeader =
        typeof actionHideHeader !== 'undefined'
          ? !!actionHideHeader
          : modalProps.hideHeader;

      const classes = classNames({
        modal: true,
        [`modal-${actionSize || size}`]: actionSize || size,
        'modal-full': type === 'full',
        'modal-forced-top-position': forcedTopPosition != null,
        [className]: className,
        ...(getClassNames ? { [getClassNames(this.props)]: true } : {}),
      });

      const overlayClasses = classNames({
        'modal-overlay': true,
        [overlayClassName]: !!overlayClassName,
      });

      return (
        <Modal
          closeTimeoutMS={timeout ? 200 : undefined}
          ariaHideApp={false}
          bodyOpenClassName="body-modal-open"
          overlayClassName={overlayClasses}
          className={classes}
          {...modalProps}
          {...this.props}
          isOpen={this.props.isOpen || this.props.forceOpen}
          onRequestClose={() => this.onClose()}
        >
          <div
            className="modal-dialog"
            style={
              forcedTopPosition
                ? {
                    top: `${forcedTopPosition}px`,
                  }
                : undefined
            }
          >
            <div className="modal-content">
              {!hideHeader && (
                <div className="modal-header">
                  {typeof modalTitle === 'string' ? (
                    <p className="modal-title">{modalTitle}</p>
                  ) : (
                    modalTitle
                  )}
                  {dismissible && (
                    <button
                      aria-label="Dismiss Modal"
                      onClick={this.onClose}
                      className="btn-dismiss btn-modal-dismiss"
                    />
                  )}
                </div>
              )}
              <div className="modal-body">
                <WrappedComponent {...this.props} />
              </div>
            </div>
          </div>
        </Modal>
      );
    }
  }

  const mapStateToProps = ({ modal }, ownProps) => ({
    isOpen: modal.dialogs[name] && modal.dialogs[name].isOpen,
    actionProps:
      ownProps.actionProps || (modal.dialogs[name] && modal.dialogs[name].props)
        ? {
            ...ownProps.actionProps,
            ...(modal.dialogs[name] && modal.dialogs[name].props),
          }
        : {},
  });

  const mapDispatchToProps = dispatch => ({
    onRequestClose: () => {
      dispatch(closeModal(name));
    },
  });

  return compose(connect(mapStateToProps, mapDispatchToProps))(
    ModalWrapperContainer,
  );
};

export default withModal;
